/**
 * BR-710 / ECSR-283: PDP size dropdown.
 *
 * Drives snippets/pdp-size-dropdown.liquid — the overlaying "Change Size"
 * dropdown that replaced the tier-tab switcher after client review — plus the
 * two header links: "Compare Sizes" (Spec & Compare dynamic compare) and the
 * Tangiblee CTA the app injects into its slot (carry-on CTA relabelled
 * "Airline Guide").
 *
 * Delegated listeners throughout: the section is re-rendered in the theme
 * editor and the same markup can be injected for quick view.
 */
(function() {
  'use strict';

  var SELECTORS = {
    root: '[data-size-dropdown]',
    trigger: '[data-size-dropdown-trigger]',
    list: '[data-size-dropdown-list]',
    option: '[data-size-option]',
    optionLink: '.pdp-size-dropdown__option-link',
    compare: '[data-compare-sizes]',
    tangibleeSlot: '[data-tangiblee-slot]',
    // Only the carry-on CTA is relabelled; the backpack CTA (Laptop
    // Compatibility) and anything else Tangiblee injects keep its own label.
    tangibleeText: '.additional-tangiblee-cta-carry-on .tangiblee-cta__text',
    // ECSR-1116: the gallery row that holds Tangiblee's primary-CTA anchor, and
    // the guide CTAs that belong in the size row rather than there.
    galleryRow: '[data-gallery-tangiblee]',
    tangibleeGuide: '.additional-tangiblee-cta-carry-on, .additional-tangiblee-cta-backpacks',
    sizeRowLinks: '.pdp-size-dropdown__links, .pdp-tangiblee-standalone'
  };

  var CLASSES = {
    open: 'pdp-size-dropdown--open',
    loading: 'is-loading'
  };

  var TANGIBLEE_LABEL = 'Airline Guide';

  /* ------------------------------------------------------------------ */
  /* Dropdown                                                            */
  /* ------------------------------------------------------------------ */

  function setOpen(root, open) {
    var trigger = root.querySelector(SELECTORS.trigger);
    root.classList.toggle(CLASSES.open, open);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function isOpen(root) {
    return root.classList.contains(CLASSES.open);
  }

  function closeAll(except) {
    document.querySelectorAll(SELECTORS.root).forEach(function(root) {
      if (root !== except && isOpen(root)) setOpen(root, false);
    });
  }

  function options(root) {
    return Array.prototype.slice.call(root.querySelectorAll(SELECTORS.optionLink));
  }

  function focusOption(root, index) {
    var links = options(root);
    if (!links.length) return;
    var i = Math.max(0, Math.min(index, links.length - 1));
    links[i].focus();
  }

  function focusedIndex(root) {
    var links = options(root);
    return links.indexOf(document.activeElement);
  }

  function open(root) {
    closeAll(root);
    setOpen(root, true);
    // Focus lands on the current size wherever it sits in the list.
    var current = root.querySelector(SELECTORS.optionLink + '[aria-current="true"]');
    if (current) {
      current.focus();
    } else {
      focusOption(root, 0);
    }
  }

  function close(root, refocus) {
    setOpen(root, false);
    if (refocus) {
      var trigger = root.querySelector(SELECTORS.trigger);
      if (trigger) trigger.focus();
    }
  }

  function select(root, option) {
    var url = option.dataset.productUrl;
    var handle = option.dataset.productHandle;
    if (!url) return;

    // Already on this product — just close.
    if (handle && root.dataset.currentHandle === handle) {
      close(root, true);
      return;
    }

    root.classList.add(CLASSES.loading);
    close(root, false);
    // Full navigation: the theme's product JS (Flickity, variant picker,
    // monogram, sticky bar) all initialise from a fresh page.
    window.location.href = url;
  }

  function onClick(e) {
    var trigger = e.target.closest(SELECTORS.trigger);
    if (trigger) {
      var root = trigger.closest(SELECTORS.root);
      if (!root) return;
      e.preventDefault();
      if (isOpen(root)) {
        close(root, true);
      } else {
        open(root);
      }
      return;
    }

    var option = e.target.closest(SELECTORS.option);
    if (option) {
      // Modified clicks (new tab, new window, download) keep the anchor's
      // native behaviour — the options are real links.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      var optRoot = option.closest(SELECTORS.root);
      if (!optRoot) return;
      e.preventDefault();
      select(optRoot, option);
      return;
    }

    var compare = e.target.closest(SELECTORS.compare);
    if (compare) {
      e.preventDefault();
      openCompare(compare.closest(SELECTORS.root));
      return;
    }

    // Click anywhere else closes any open list.
    if (!e.target.closest(SELECTORS.root)) closeAll(null);
  }

  function onKeydown(e) {
    var root = e.target.closest(SELECTORS.root);
    if (!root) return;

    var onTrigger = !!e.target.closest(SELECTORS.trigger);
    var option = e.target.closest(SELECTORS.option);
    // Only the trigger and the options drive the list. The header links
    // (Compare Sizes, Tangiblee's CTA) live inside the root too and keep their
    // native key handling.
    if (!onTrigger && !option) return;

    switch (e.key) {
      case 'Escape':
        if (isOpen(root)) {
          e.preventDefault();
          close(root, true);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen(root)) {
          open(root);
        } else {
          focusOption(root, focusedIndex(root) + 1);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen(root)) {
          open(root);
        } else {
          focusOption(root, focusedIndex(root) - 1);
        }
        break;

      case 'Home':
        if (isOpen(root)) {
          e.preventDefault();
          focusOption(root, 0);
        }
        break;

      case 'End':
        if (isOpen(root)) {
          e.preventDefault();
          focusOption(root, options(root).length - 1);
        }
        break;

      case 'Enter':
      case ' ':
        if (option) {
          e.preventDefault();
          select(root, option);
        } else if (onTrigger && e.key === ' ') {
          // Enter on a <button> already fires click; Space needs the same.
          e.preventDefault();
          if (isOpen(root)) {
            close(root, true);
          } else {
            open(root);
          }
        }
        break;

      case 'Tab':
        // Tabbing out of the list closes it without trapping focus.
        if (isOpen(root) && !onTrigger) close(root, false);
        break;
    }
  }

  /*
    Focus leaving the dropdown for *nothing* does not close it.

    Safari — on iOS and on the desktop — does not focus a <button> when it is
    clicked or tapped. Activating the trigger of an open list therefore drops
    focus off the focused option to nowhere, firing focusout with a null
    relatedTarget just before the click: focusout closed the list and the click
    that followed re-opened it, so the list could not be dismissed by tapping
    the trigger (client QA, 1 Sep). Chromium focuses the button, which is why
    Chrome and its device emulation never showed it.

    Closing on a pointer belongs to onClick, which owns the toggle and already
    closes on an outside click; keyboard exit always carries a relatedTarget and
    is additionally handled by the Tab case in onKeydown. So only a real focus
    move to an element outside the dropdown closes it here.
  */
  function onFocusOut(e) {
    var root = e.target.closest ? e.target.closest(SELECTORS.root) : null;
    if (!root || !isOpen(root)) return;
    var next = e.relatedTarget;
    if (next && !root.contains(next)) close(root, false);
  }

  /* ------------------------------------------------------------------ */
  /* Compare Sizes — Spec & Compare dynamic compare                      */
  /* ------------------------------------------------------------------ */

  /*
    Spec & Compare's app embed renders two custom elements:
      <sc-dynamic-compare>         — the queue: addProduct / containsProduct /
                                     clearProducts / getHandles
      <sc-dynamic-compare-dialog>  — the modal, exposing dialogElement (a <dialog>
                                     in its shadow root) once it has upgraded.
    The app's own floating "Compare" button opens the dialog the same way. Both
    elements must be present and upgraded before the link is shown, so it never
    renders as a dead control on a store where the embed is off.
  */
  function dynamicCompare() {
    var queue = document.querySelector('sc-dynamic-compare');
    var dialog = document.querySelector('sc-dynamic-compare-dialog');
    if (!queue || typeof queue.addProduct !== 'function') return null;
    if (!dialog || !dialog.dialogElement || typeof dialog.dialogElement.showModal !== 'function') return null;
    return { queue: queue, dialog: dialog };
  }

  function revealCompareLinks() {
    var available = !!dynamicCompare();
    document.querySelectorAll(SELECTORS.compare).forEach(function(btn) {
      btn.hidden = !available;
    });
    return available;
  }

  function openCompare(root) {
    var sc = dynamicCompare();
    if (!sc) return;

    var handles = ((root && root.dataset.familyHandles) || '')
      .split(',')
      .map(function(h) { return h.trim(); })
      .filter(Boolean);

    // Compare exactly this size family: clear anything the shopper had queued
    // from a collection page, then queue every size, current one included.
    if (typeof sc.queue.clearProducts === 'function') sc.queue.clearProducts();
    handles.forEach(function(handle) {
      if (typeof sc.queue.containsProduct !== 'function' || !sc.queue.containsProduct(handle)) {
        sc.queue.addProduct(handle);
      }
    });

    try {
      sc.dialog.dialogElement.showModal();
    } catch (err) {
      // Already open, or not attached — nothing further to do.
    }
  }

  /*
    The embed's script is async and may upgrade its elements after this one runs,
    so the reveal is retried on the element definitions, on load, and for a short
    while after — then stops.
  */
  function scheduleCompareReveal() {
    if (revealCompareLinks()) return;
    if (window.customElements && customElements.whenDefined) {
      ['sc-dynamic-compare', 'sc-dynamic-compare-dialog'].forEach(function(tag) {
        customElements.whenDefined(tag).then(revealCompareLinks).catch(function() {});
      });
    }
    var attempts = 0;
    var timer = setInterval(function() {
      attempts += 1;
      if (revealCompareLinks() || attempts >= 20) clearInterval(timer);
    }, 500);
  }

  /* ------------------------------------------------------------------ */
  /* Airline Guide — Tangiblee                                           */
  /* ------------------------------------------------------------------ */

  /*
    Tangiblee builds its carry-on CTA with the title configured in its
    dashboard ("Airline Compatibility"). The client's reference reads "Airline
    Guide"; that label is rewritten here once the CTA lands in its slot. Only
    the carry-on CTA is touched: the backpack CTA ("Laptop Compatibility") opens
    a different widget and keeps Tangiblee's label. Owning the label in
    Tangiblee's dashboard instead would make this redundant.
  */
  function relabelTangiblee(slot) {
    slot.querySelectorAll(SELECTORS.tangibleeText).forEach(function(text) {
      if (text.textContent !== TANGIBLEE_LABEL) text.textContent = TANGIBLEE_LABEL;
    });
  }

  function watchTangiblee() {
    var slots = document.querySelectorAll(SELECTORS.tangibleeSlot);
    if (!slots.length) return;
    slots.forEach(function(slot) {
      relabelTangiblee(slot);
      if (!window.MutationObserver) return;
      new MutationObserver(function() { relabelTangiblee(slot); })
        .observe(slot, { childList: true, subtree: true, characterData: true });
    });
  }

  /*
    ECSR-1116: Tangiblee's primary-CTA anchor lives in the gallery's "See
    Product Size" row (snippets/pdp-product-images.liquid). Its guide CTAs
    (Airline / Laptop Compatibility) append to their [data-tangiblee-slot] div
    when one is on the page; when none matches, its
    bundle falls back to placing them right after the primary CTA — which would
    put them in the gallery. Any that land there are returned to the size row:
    into a slot if there is one, otherwise into the row itself, ahead of Compare
    Sizes. Tangiblee re-creates its CTAs when it re-shows them, so the row is
    watched rather than checked once.
  */
  function sizeRowTarget() {
    var slot = document.querySelector(SELECTORS.tangibleeSlot);
    if (slot) return { parent: slot, before: null };
    var row = document.querySelector(SELECTORS.sizeRowLinks);
    if (!row) return null;
    return { parent: row, before: row.querySelector(SELECTORS.compare) };
  }

  function returnTangibleeGuides(galleryRow) {
    var guides = galleryRow.querySelectorAll(SELECTORS.tangibleeGuide);
    if (!guides.length) return;
    var target = sizeRowTarget();
    if (!target) return;
    guides.forEach(function(guide) {
      target.parent.insertBefore(guide, target.before);
    });
    relabelTangiblee(target.parent);
  }

  /*
    ECSR-1116 rework: the gallery CTA's visible "See Size" label is CSS (see
    snippets/pdp-product-images.liquid). Should Tangiblee also give its CTA an
    aria-label, that would still announce "See Product Size", so an existing
    aria-label is brought in line with the row's data-tangiblee-label. Only an
    existing one: a generic element must not gain an aria-label. Attribute
    writes do not trigger the row's childList observer, and the value is only
    written when it differs, so this cannot re-trigger itself.
  */
  function labelGalleryCta(galleryRow) {
    var label = galleryRow.getAttribute('data-tangiblee-label');
    if (!label) return;
    galleryRow.querySelectorAll('.tangiblee-cta[aria-label]').forEach(function(cta) {
      if (cta.getAttribute('aria-label') !== label) cta.setAttribute('aria-label', label);
    });
  }

  function watchGalleryRow() {
    document.querySelectorAll(SELECTORS.galleryRow).forEach(function(galleryRow) {
      var update = function() {
        returnTangibleeGuides(galleryRow);
        labelGalleryCta(galleryRow);
      };
      update();
      if (!window.MutationObserver) return;
      new MutationObserver(update).observe(galleryRow, { childList: true });
    });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusout', onFocusOut);

    scheduleCompareReveal();
    window.addEventListener('load', revealCompareLinks);

    watchTangiblee();
    watchGalleryRow();

    // Theme editor: section re-render replaces the markup.
    document.addEventListener('shopify:section:load', function() {
      scheduleCompareReveal();
      watchTangiblee();
      watchGalleryRow();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
